import type { CaptureResult, PostHogConfig } from "posthog-js";
import { isStaleClientError } from "@/lib/stale-client";

const DEFAULT_POSTHOG_API_HOST = "https://us.i.posthog.com";

type PostHogExceptionItem = {
  type?: string;
  value?: string;
};

type PostHogBrowserConfigInput = {
  apiHost?: string;
  nodeEnv?: string;
};

export function shouldInitializePostHog(enabled?: string): boolean {
  return enabled === "true";
}

export function filterPostHogBrowserEvent(
  event: CaptureResult | null
): CaptureResult | null {
  if (event?.event === "$autocapture") {
    return null;
  }

  if (event?.event !== "$exception") {
    return event;
  }

  const exception = (
    event.properties as { $exception_list?: PostHogExceptionItem[] } | undefined
  )?.$exception_list?.[0];
  const type = exception?.type;
  const value = exception?.value;

  if (
    (typeof value === "string" && value.includes("ResizeObserver loop")) ||
    value === "Script error." ||
    (type === "InvalidStateError" &&
      typeof value === "string" &&
      value.includes("View Transition")) ||
    (typeof value === "string" && value.includes("Java object is gone")) ||
    isStaleClientError(value) ||
    isStaleClientError(type)
  ) {
    return null;
  }

  return event;
}

export function createPostHogBrowserConfig({
  apiHost,
  nodeEnv,
}: PostHogBrowserConfigInput): Partial<PostHogConfig> {
  return {
    api_host: apiHost ?? DEFAULT_POSTHOG_API_HOST,
    ui_host: "https://us.posthog.com",
    defaults: "2025-05-24",
    before_send: filterPostHogBrowserEvent,
    capture_exceptions: {
      capture_console_errors: false,
      capture_unhandled_errors: true,
      capture_unhandled_rejections: true,
    },
    // Keep the PostHog cookie on .lapyme.com.ar. The marketing site uses the
    // same project and cookie, so sign-up merges the visitor's anonymous ID.
    cross_subdomain_cookie: true,
    debug: nodeEnv === "development",
    disable_session_recording: true,
  };
}
